/**
 * Maintenance.
 *
 * **Sourced, and the best-evidenced form in the set on both sides.** Question text and display
 * logic come from `Maintenance_Survey.qsf` (`SV_cMDKl2AuXQaMbJ4`, 44,232 responses, 124 questions,
 * 39 photo uploads). The Salesforce side is stronger still: the survey's enabled workflow
 * (`Salesforce Integration - Copy`) was captured writing 111 fields to `Work_Submission__c` under
 * record type `012at000000LfjJAAS`, so most mappings below are observed integrations rather than
 * name matches against a describe.
 *
 * It is also where the saving is worth the most: maintenance is 61,461 interventions in the last
 * 180 days, roughly 74.5% of all field work.
 *
 * Three levers reduce the form, and they are separate things that must not be added together:
 *
 *  1. **Prefill** — the eleven parity values plus MAC address and installation method. The source
 *     survey receives `Kiosk_ID`, `Locker_Gen`, `Locker_Layout`, `Indoor_outdoor`, `Locker_Type`,
 *     `Locker_Name`, `Country_code`, `Supplier_Email` as URL parameters and asks the technician to
 *     retype most of them. That is the whole Locker Information block, gone.
 *  2. **Display logic** — `ctx.lockerPlatform` alone gates the CORE and ODIN blocks. It is derived
 *     from `Access_Point_Type__c` at 100% fill, so the platform never has to be asked. In the
 *     source, `Locker_model` is an embedded field the launching system must remember to set.
 *  3. **Capture, not typing** — the source has 21 free-text serial fields (`Starter_SN`,
 *     `Adder_1_SN` … `Adder_20_SN`). Serial capture belongs to the module screen, so they are not
 *     reproduced as text questions here.
 *
 * The source's 20-question Trash block is not reproduced.
 *
 * **Reused `DataExportTag`s, and why they are kept.** The source survey reuses tags across blocks.
 * Two of those collisions survive into this schema and are deliberate:
 *
 *  - `Q101` → `not_anchored_photo` (Anchoring) and `rj45_led` (For Core)
 *  - `Q104` → `kiosk_id` (Visit Information) and `sim_iccid` (For Core)
 *
 * The field **ids** are distinct, so no answer overwrites another; only the `sourceTag` repeats.
 * A `sourceTag` is a pointer back into the source artefact, and inventing a unique-looking tag
 * would point at nothing — it would make the schema look tidier while destroying the only trace a
 * reviewer has for finding the original question. So the duplicates are recorded here instead:
 * the ambiguity is upstream, in `Maintenance_Survey.qsf`, and this is where it is disclosed.
 */

import type { FormSchema } from '../types.ts';

const WS = 'Work_Submission__c';
const WORKFLOW =
  'discovery/qualtrics/10-WORKFLOWS-AND-SF-MAPPING.md — Maintenance Survey, workflow "Salesforce Integration - Copy", Insert Work_Submission__c record type 012at000000LfjJAAS, 111 fields, ENABLED';
const DESCRIBE = 'discovery/salesforce/field-dumps.txt (Work_Submission__c describe)';

/** Observed in the captured live workflow. */
function mapped(field: string, type: string, caveat?: string) {
  return {
    object: WS,
    field,
    type,
    confidence: 'verified' as const,
    evidence: WORKFLOW,
    ...(caveat !== undefined ? { caveat } : {}),
  };
}

/** In the describe but not in the captured workflow. */
function described(field: string, type: string, caveat?: string) {
  return {
    object: WS,
    field,
    type,
    confidence: 'verified' as const,
    evidence: `${DESCRIBE}. Not among the 111 fields the Maintenance workflow writes today.`,
    ...(caveat !== undefined ? { caveat } : {}),
  };
}

const IS_CORE = { field: 'ctx.lockerPlatform', op: 'eq' as const, value: 'CORE' };
const IS_ODIN = { field: 'ctx.lockerPlatform', op: 'eq' as const, value: 'ODIN' };
const IS_OUTDOOR = { field: 'placement', op: 'eq' as const, value: 'Outdoor' };
const IS_RESIDENTIAL = { field: 'ctx.lockerType', op: 'eq' as const, value: 'Residential' };

const CORE_PARTS = [
  'Control board',
  'Screen',
  'Barcode reader',
  'Keypad',
  'Power supply',
  'UPS battery',
  'Door / slot lock',
  'Cable',
  'Other',
] as const;

const ODIN_PARTS = [
  'IPC',
  'Modem / router',
  'Antenna',
  'Screen',
  'Control board',
  'Battery',
  'Door / slot lock',
  'Cable',
  'Other',
] as const;

const CONDITION_SCOPE = ['No', 'One module', 'Several modules', 'The whole locker'] as const;

export const MAINTENANCE: FormSchema = {
  id: 'maintenance',
  version: 1,
  title: 'Maintenance',
  source: {
    provenance: 'sourced',
    artefact: 'Maintenance_Survey.qsf — Qualtrics SV_cMDKl2AuXQaMbJ4 (44,232 responses, 124 questions, 39 photo uploads)',
    sourceQuestionCount: 124,
    note:
      'Question text from discovery/qualtrics/Maintenance_Survey.md, including its typos and inconsistent capitalisation, so wording can be diffed against the source. Section titles are the survey\'s own block names. Display logic reproduces the real chains, including the ones branching on embedded data (Locker_model, Country_code, Locker_Type) which arrive here as ctx.* values derived from the kiosk record. The 21 free-text serial fields and the 20-question Trash block are not reproduced. ELEVEN option lists here are inferred rather than sourced, not the part-name lists alone: the source choice sets were not recoverable from the parsed export. Counted 2026-09-03 and asserted in test/forms/schema-defects.test.ts — 5 of the 11 still carry a source tag, so the question is traceable even though its choices are not (rusted Q81, dirty Q85, stickers Q87, health_confirmed Q75, ots_wait Q120), and 6 have no tag at all, so both the question and its choices are inferred (revisit_reason, parts_ordered_core, parts_ordered_odin, parts_used_core, parts_used_odin, ots_channel). Only 4 of the 11 are part-name lists; describing them as "the one place" understated the gap by more than half.',
  },
  destination: {
    object: 'Work_Submission__c',
    recordTypeId: '012at000000LfjJAAS',
    recordTypeLabel: 'Maintenance',
    confidence: 'verified',
    evidence: WORKFLOW,
  },
  sections: [
    {
      id: 'visit-information',
      title: 'Visit Information',
      subtitle: 'The identifiers are from the record. The reason for dispatch is the first thing worth asking.',
      fields: [
        {
          id: 'kiosk_id',
          kind: 'text',
          label: 'Enter the Kiosk ID:',
          required: true,
          prefill: { source: 'kiosk.id' },
          sourceTag: 'Q104',
          maps: mapped('Kiosk_ID__c', 'double', 'The workflow maps this as a double even though the kiosk id is an 8-character string on OpportunityLineItem. Leading zeros are lost. Send as a string; the describe types the Work_Submission__c field as string(255).'),
        },
        {
          id: 'simt_id',
          kind: 'text',
          label: 'Enter the SIMT ID:',
          required: true,
          prefill: { source: 'intervention.postInstallId' },
          sourceTag: 'Q105',
          maps: mapped('Ticket_ID__c', 'string(255)'),
        },
        {
          id: 'vendor_reference',
          kind: 'text',
          label: 'Enter the external vendor ID (Ticket/Call/Reference#):',
          prefill: { source: 'intervention.supplierWorkOrderId' },
          sourceTag: 'Q2',
          maps: mapped('Vendor_reference_number__c', 'string(255)', 'On the PWA write allowlist.'),
        },
        {
          id: 'visit_start',
          kind: 'time',
          label: 'Enter the Visit Start Time with 24-hour format (HH:MM):',
          required: true,
          prefill: { source: 'visit.startTime' },
          validate: { pattern: '([01][0-9]|2[0-3]):[0-5][0-9]', patternMessage: 'Use HH:MM, 24-hour' },
          sourceTag: 'Q3',
          maps: mapped('Visit_Start_Time__c', 'time', 'The describe types this string(30), not a DateTime. Send "HH:MM".'),
        },
        {
          id: 'dispatch_reason_core',
          kind: 'single',
          label: 'What is the reason for dispatch (CORE)?',
          options: [
            'Power Outage',
            'Lost Network Connection',
            'Screen Damaged',
            'PCB Damaged',
            'Barcode Reader damaged',
            'Spare part replacement',
            'Preventive Cleaning',
            'Other',
          ],
          required: true,
          showIf: IS_CORE,
          prefill: { source: 'intervention.dispatchReason', overrideHint: 'Correct it if the ticket got the reason wrong — that mismatch is worth recording.' },
          sourceTag: 'Q4',
          maps: mapped('Dispatch_Reason_Core__c', 'picklist (8 values)'),
        },
        {
          id: 'dispatch_reason_odin',
          kind: 'single',
          label: 'What is the reason for dispatch (ODIN)?',
          options: [
            'Power Outage',
            'Lost Network Connection',
            'Screen Damaged',
            'IPC Down',
            'PCB Damaged',
            'Barcode Reader damaged',
            'LPWan outage',
            'BLE outage',
            'Battery outage',
            'Certificate renewal',
            'Transient network issue',
            'Spare part replacement',
            'Preventive Cleaning',
            'Graffiti Cleaning',
            'Pest/Plague Cleaning',
            'Deep Cleaning',
            'Other',
          ],
          required: true,
          showIf: IS_ODIN,
          prefill: { source: 'intervention.dispatchReason', overrideHint: 'Correct it if the ticket got the reason wrong.' },
          sourceTag: 'Q5',
          maps: mapped('Dispatch_Reason_ODIN__c', 'picklist'),
        },
        {
          id: 'dispatch_reason_other',
          kind: 'textarea',
          label: 'What is the other reasons for dispatch?',
          required: true,
          showIf: {
            any: [
              { field: 'dispatch_reason_core', op: 'eq', value: 'Other' },
              { field: 'dispatch_reason_odin', op: 'eq', value: 'Other' },
            ],
          },
          validate: { maxLength: 3000 },
          sourceTag: 'Q6',
          maps: mapped('Dispatch_Reason_for_Other__c', 'string'),
        },
        {
          id: 'ttk_used',
          kind: 'yesno',
          label: 'Did you use TTK (TextToKiosk) to confirm locker health before visit?',
          required: true,
          sourceTag: 'Q16',
          maps: mapped('TTK_confirmed_at_beginning__c', 'picklist'),
        },
        {
          id: 'locker_healthy',
          kind: 'yesno',
          label: 'Was the locker in a healthy operational state?',
          required: true,
          sourceTag: 'Q17',
          help: 'If it was already healthy on arrival, this trip may have been preventable. That is worth recording accurately.',
          maps: mapped('Was_the_locker_healthy__c', 'picklist'),
        },
        {
          id: 'drive_time',
          kind: 'number',
          label: 'Please enter your drive time to get to site (in minutes)',
          validate: { integer: true, min: 0, max: 1440 },
          sourceTag: 'Q121',
          maps: mapped('Total_Drive_Time__c', 'double'),
        },
      ],
    },

    {
      id: 'locker-information',
      title: 'Locker Information',
      subtitle:
        'Every question in this block is prefilled. In the source survey all seven are asked, and all seven are already handed to the form as URL parameters.',
      fields: [
        {
          id: 'generation',
          kind: 'single',
          label: 'Select the physical locker generation:',
          options: ['2', '2.5', '3', '4', '5'],
          required: true,
          prefill: { source: 'kiosk.generation' },
          sourceTag: 'Q11',
          maps: mapped('Locker_Generation__c', 'picklist 2|2.5|3|4|5', 'OpportunityLineItem holds 3.0 / GEN_3.0 / 4.0; this field holds 3 / 4. Normalise on prefill or the value will not match.'),
        },
        {
          id: 'locker_name',
          kind: 'text',
          label: 'Enter the locker name as shown on the sticker:',
          required: true,
          prefill: { source: 'kiosk.name', overrideHint: 'The sticker is the authority here. If it disagrees with the record, the record is wrong.' },
          sourceTag: 'Q9',
          help:
            'This is the one identifier that is physically on the hardware. The 8-character Kiosk ID is not printed anywhere on the locker, which is why it can only ever be prefilled.',
          maps: mapped('Locker_Name__c', 'string(255)'),
        },
        {
          id: 'layout',
          kind: 'text',
          label: 'Enter the actual locker layout using "S" for Starter and "A" for Adder, or "M" for Gen_4 module:',
          required: true,
          prefill: { source: 'kiosk.layout', overrideHint: 'Correct it if modules have been added or removed since.' },
          sourceTag: 'Q12',
          validate: { minLength: 1, maxLength: 255 },
          maps: mapped('Locker_Layout__c', 'picklist (15 values)', 'Restricted to 15 values against a field the survey collects as free text. Anything outside them will not write.'),
        },
        {
          id: 'placement',
          kind: 'single',
          label: 'Is the locker indoor or outdoor:',
          options: ['Indoor', 'Outdoor'],
          required: true,
          prefill: { source: 'kiosk.placement' },
          sourceTag: 'Q13',
          help: 'Gates roughly six downstream photo questions, so prefilling it collapses branching as well as saving a tap.',
          maps: mapped('Indoor_outdoor__c', 'picklist Indoor|Outdoor', 'C-68: on this record type, Indoor_outdoor__c = Outdoor together with Anchor_Type__c = No fires Review_Anchoring_Task through flow WorkSubmission_afterUpdate_CreateTask, which has no ISNEW/ISCHANGED guard, and sends real outbound email from no-reply-support@the-hub.amazon.com. This pair must never be written speculatively or on a replay.'),
        },
        {
          id: 'mount_type',
          kind: 'single',
          label: 'What is the Location Type?',
          options: ['Back to Back', 'Free Standing', 'Wall Mount', 'Window Mount', 'Floor Mount'],
          required: true,
          prefill: { source: 'kiosk.mountType' },
          sourceTag: 'Q14',
          maps: mapped('Location_Type__c', 'picklist', 'OpportunityLineItem uses FREE_STANDING / BACK_TO_BACK; this field uses "Free Standing" / "Back to Back". Normalise both ways.'),
        },
        {
          id: 'install_method',
          kind: 'multi',
          label: 'What is the locker Installation Method?',
          options: [
            'Side Anchor',
            'Anchoring by each foot',
            'Fall Prevention Board',
            'Perforated Skids',
            'On field concrete base',
            'Pre-build concrete base',
            'Wall anchoring',
            'Ground Bracket',
            'Concrete Block',
            'None',
          ],
          required: true,
          prefill: { source: 'case.installationMethod' },
          sourceTag: 'Q15',
          help: 'Gates at least five anchoring-photo questions. Only 8% of cases carry it, so it is a suggestion rather than an answer for most kiosks.',
          maps: mapped('Installation_Method__c', 'multipicklist (10 values)', 'Case.Installation_Method__c offers only 6 of these 10.'),
        },
        {
          id: 'occupancy',
          kind: 'single',
          label: 'Is the locker Residential/Commercial:',
          options: ['Commercial', 'Residential'],
          required: true,
          prefill: { source: 'intervention.lockerType' },
          sourceTag: 'Q108',
          help: 'Gates the Residential block. 100% populated on actionable interventions, so that block never needs a question to reach it.',
          maps: mapped('Locker_Type__c', 'picklist', 'The picklist mixes product line and occupancy values; only Commercial and Residential are meant here.'),
        },
      ],
    },

    {
      id: 'physical-locker-check',
      title: 'Physical Locker Check',
      fields: [
        {
          id: 'safety_risk',
          kind: 'yesno',
          label: 'Any safety risks (e.g., bent bollards, holes, etc.)?',
          required: true,
          sourceTag: 'Q55',
          maps: mapped('Safety_risk__c', 'picklist'),
        },
        {
          id: 'safety_risk_photo',
          kind: 'photo',
          label: 'safety risks picture',
          required: true,
          showIf: { field: 'safety_risk', op: 'eq', value: true },
          validate: { minPhotos: 1, maxPhotos: 5 },
          sourceTag: 'Q56',
          maps: mapped('Picture_of_safety_risks__c', 'url'),
        },
        {
          id: 'vandalized',
          kind: 'multi',
          label: 'Is the locker vandalized?',
          options: ['No', 'Stickers', 'Graffiti', 'Structural damages'],
          required: true,
          validate: { minSelections: 1 },
          sourceTag: 'Q79',
          maps: mapped('Is_the_locker_vandalized__c', 'multipicklist Stickers|Graffiti|No|Structural damages'),
        },
        {
          id: 'vandalized_photo',
          kind: 'photo',
          label: 'Please provide a picture of vandalized part',
          required: true,
          showIf: { field: 'vandalized', op: 'excludes', value: ['No'] },
          sourceTag: 'Q80',
          maps: mapped('Locker_Vandalized_Pic__c', 'url'),
        },
        {
          id: 'rusted',
          kind: 'multi',
          label: 'Is the locker rusted?',
          options: [...CONDITION_SCOPE],
          required: true,
          validate: { minSelections: 1 },
          sourceTag: 'Q81',
          inferred: true,
          help: 'Option wording is inferred; the source choice list was not recoverable from the parsed export.',
          maps: mapped('Rust_visible_on_ground_or_wall_brackets__c', 'multipicklist'),
        },
        {
          id: 'rusted_photo',
          kind: 'photo',
          label: 'Please provide a picture of rusted part',
          required: true,
          showIf: { field: 'rusted', op: 'excludes', value: ['No'] },
          sourceTag: 'Q82',
          maps: mapped('Upload_images_of_rusted_structure__c', 'url'),
        },
        {
          id: 'damaged',
          kind: 'multi',
          label: 'Is the locker damaged?',
          options: ['No', 'Scratches', 'Dents', 'Structural damage'],
          required: true,
          validate: { minSelections: 1 },
          sourceTag: 'Q83',
          maps: mapped('any_visible_damage_on_the_locker__c', 'multipicklist Yes|No|Scratches|Dents', 'Do not confuse with any_visable_damage_on_the_locker__c, a boolean twin whose field label is literally "delete".'),
        },
        {
          id: 'damaged_photo',
          kind: 'photo',
          label: 'Please provide a picture of damage',
          required: true,
          showIf: { field: 'damaged', op: 'excludes', value: ['No'] },
          validate: { minPhotos: 1, maxPhotos: 5 },
          sourceTag: 'Q84',
          maps: mapped('Take_photos_of_damage__c', 'url'),
        },
        {
          id: 'dirty',
          kind: 'multi',
          label: 'Is the Locker dirty?',
          options: [...CONDITION_SCOPE],
          required: true,
          validate: { minSelections: 1 },
          sourceTag: 'Q85',
          inferred: true,
          maps: mapped('Is_the_Locker_dirty__c', 'multipicklist'),
        },
        {
          id: 'dirty_photo',
          kind: 'photo',
          label: 'Please provide a picture of dirty part',
          required: true,
          showIf: { field: 'dirty', op: 'excludes', value: ['No'] },
          sourceTag: 'Q86',
          maps: mapped('Locker_Dirty_Pic__c', 'url'),
        },
        {
          id: 'stickers',
          kind: 'multi',
          label: 'Are there missing/damaged sticker?',
          options: ['No', 'Amazon logo', 'Call to action', 'CS sticker', 'Locker name'],
          required: true,
          validate: { minSelections: 1 },
          sourceTag: 'Q87',
          inferred: true,
          maps: mapped('One_or_several_sticker_incompliant__c', 'multipicklist'),
        },
        {
          id: 'stickers_photo',
          kind: 'photo',
          label: 'Please provide a picture of missing/damaged sticker',
          required: true,
          showIf: { field: 'stickers', op: 'excludes', value: ['No'] },
          sourceTag: 'Q88',
          maps: mapped('Incompliant_sticker_pic__c', 'url'),
        },
        {
          id: 'wrapped',
          kind: 'yesno',
          label: 'Is the locker wrapped?',
          required: true,
          sourceTag: 'Q89',
          maps: mapped('Is_the_Locker_wrapped__c', 'picklist'),
        },
        {
          id: 'wrap_condition',
          kind: 'single',
          label: 'What are the wrap conditions',
          options: ['Good', 'With scratches', 'Poor'],
          required: true,
          showIf: { field: 'wrapped', op: 'eq', value: true },
          sourceTag: 'Q90',
          maps: mapped('Is_the_wrap_damaged__c', 'picklist Yes|No|Good|With scratches|Poor'),
        },
        {
          id: 'visible_from_street',
          kind: 'yesno',
          label: 'Is the locker visible from the street?',
          required: true,
          showIf: IS_OUTDOOR,
          sourceTag: 'Q91',
          maps: mapped('Is_the_Locker_visible_from_the_street__c', 'picklist'),
        },
        {
          id: 'found_easily',
          kind: 'yesno',
          label: 'Did you find the locker easily with the address you were given?',
          required: true,
          sourceTag: 'Q103',
          help: 'The record already carries customer-facing directions for essentially every kiosk. A No here means those directions are wrong.',
          maps: mapped('Did_you_find_the_locker_easily__c', 'picklist'),
        },
        {
          id: 'locker_location_description',
          kind: 'textarea',
          label: 'If so, please describe where the locker is situated',
          required: true,
          showIf: { field: 'found_easily', op: 'eq', value: false },
          validate: { maxLength: 1000 },
          sourceTag: 'Q111',
          maps: mapped('Locker_location_description__c', 'textarea'),
        },
      ],
    },

    {
      id: 'anchoring',
      title: 'Anchoring',
      fields: [
        {
          id: 'anchor_type',
          kind: 'single',
          label: 'What is the anchor type?',
          options: ['Floor', 'Wall', 'Both', 'No'],
          required: true,
          prefill: { source: 'history.anchorType', overrideHint: 'This is the last visit\u2019s assertion, not verified truth. Check the hardware.' },
          help:
            'The source survey receives this as an Anchortype URL parameter rather than asking. Nothing on the kiosk record holds it either \u2014 the only anchoring field, Locker_Bolting_Pics__c, is 0.0% populated \u2014 so the best available prefill is what the last visit recorded.',
          sourceTag: 'Anchortype (embedded data)',
          maps: mapped('Anchor_Type__c', 'picklist Floor|Wall|Both|No', 'C-68: "No" together with Indoor_outdoor__c = Outdoor on this record type sends real outbound email through an unguarded flow. Both fields are on the PWA write allowlist and both are tagged OUTWARD_EMAIL_TRIGGER_FIELDS in infra/src/writer/field-policy.ts for exactly this reason.'),
        },
        {
          id: 'not_anchored_photo',
          kind: 'photo',
          label: 'If not anchored, please provide images to show this.',
          required: true,
          showIf: { field: 'anchor_type', op: 'eq', value: 'No' },
          sourceTag: 'Q101',
          maps: mapped('If_not_anchored_please_provide_image__c', 'url'),
        },
        {
          id: 'photo_left_ground_anchor',
          kind: 'photo',
          label: 'Photo from the left ground anchoring',
          required: true,
          showIf: { field: 'anchor_type', op: 'in', value: ['Floor', 'Both'] },
          sourceTag: 'Q59',
          maps: mapped('Photo_of_left_anchor__c', 'url'),
        },
        {
          id: 'photo_right_ground_anchor',
          kind: 'photo',
          label: 'Photo from the right ground anchoring',
          required: true,
          showIf: { field: 'anchor_type', op: 'in', value: ['Floor', 'Both'] },
          sourceTag: 'Q60',
          maps: mapped('Photo_of_right_anchor__c', 'url'),
        },
        {
          id: 'photo_left_wall_anchor',
          kind: 'photo',
          label: 'Photo from the left wall anchoring',
          required: true,
          showIf: { field: 'anchor_type', op: 'in', value: ['Wall', 'Both'] },
          sourceTag: 'Q61',
          maps: mapped('Photo_from_the_left_wall_anchor__c', 'url'),
        },
        {
          id: 'photo_right_wall_anchor',
          kind: 'photo',
          label: 'Photo from the right wall anchoring',
          required: true,
          showIf: { field: 'anchor_type', op: 'in', value: ['Wall', 'Both'] },
          sourceTag: 'Q62',
          maps: mapped('Photo_from_the_right_wall_anchor__c', 'url'),
        },
        {
          id: 'photo_intermediate_anchor',
          kind: 'photo',
          label: 'Photo from the intermediate anchoring',
          /*
           * ⚠️ THIS STRING USED TO READ "This locker has {intermediateAnchorCount} of them, derived
           * from the layout." Nothing substitutes `{…}`. `FieldControl.tsx` renders `field.help`
           * verbatim and `localiseSchema.ts` passes it through a `Translator` that has no
           * interpolation, so a technician read the literal token `{intermediateAnchorCount}`.
           *
           * The count IS available — `deriveContext` in `prefill.ts` publishes
           * `ctx.intermediateAnchorCount`, and the `showIf` immediately below reads it — so this was
           * a missing capability, not missing data. Until the render path can interpolate (see the
           * note in `test/forms/schema-defects.test.ts`), the wording states the RULE rather than
           * the instance, which needs no substitution and is what `install.ts`'s twin of this same
           * question already does.
           */
          help: 'One frame per join between adjacent columns. A five-column locker has four joins.',
          showIf: { field: 'ctx.intermediateAnchorCount', op: 'gte', value: 1 },
          validate: { maxPhotos: 5 },
          sourceTag: 'Q63',
          maps: mapped('Photo_from_the_intermediate_anchorings_1__c', 'url', 'Five numbered slots exist (…_1__c … _5__c). A locker with more than five joins cannot be fully recorded. In the source this count is derived as Locker_Layout_Length - 1; here it comes from Hub_Size__c.'),
        },
        {
          id: 'bolts_present',
          kind: 'number',
          label: 'Number of bolts present',
          required: true,
          showIf: { field: 'anchor_type', op: 'neq', value: 'No' },
          validate: { integer: true, min: 0, max: 200 },
          sourceTag: 'Q122',
          maps: mapped('Number_of_bolts_wall_mounts_installed__c', 'double'),
        },
        {
          id: 'side_screws_present',
          kind: 'number',
          label: 'Number of side screws present',
          showIf: { field: 'mount_type', op: 'eq', value: 'Back to Back' },
          validate: { integer: true, min: 0, max: 200 },
          sourceTag: 'Q123',
          maps: mapped('Number_of_side_screws_installed__c', 'double'),
        },
        {
          id: 'all_screws_present',
          kind: 'yesno',
          label: 'Are all screws present?',
          required: true,
          showIf: { field: 'anchor_type', op: 'neq', value: 'No' },
          sourceTag: 'Q124',
          maps: mapped('All_screws_present__c', 'picklist'),
        },
        {
          id: 'concrete_base_photo',
          kind: 'photo',
          label: 'Photo of the concrete base',
          showIf: IS_OUTDOOR,
          sourceTag: 'Q102',
          maps: mapped('Photo_of_the_concrete_base__c', 'url'),
        },
        {
          id: 'shelter_present',
          kind: 'yesno',
          label: 'Does the locker have a shelter/lightbox?',
          required: true,
          showIf: IS_OUTDOOR,
          sourceTag: 'Q64',
          maps: mapped('Shelter_installed__c', 'picklist'),
        },
        {
          id: 'shelter_working',
          kind: 'yesno',
          label: 'Is the shelter light/light box working?',
          required: true,
          showIf: { field: 'shelter_present', op: 'eq', value: true },
          sourceTag: 'Q65',
          maps: mapped('Are_the_shelter_light_light_box_working__c', 'picklist'),
        },
        {
          id: 'shelter_photo',
          kind: 'photo',
          label: 'Provide a picture the shelter light/light box',
          required: true,
          showIf: { field: 'shelter_present', op: 'eq', value: true },
          sourceTag: 'Q66',
          maps: mapped('Shelter_Lighbox_pic__c', 'url', 'Field API name has the typo "Lighbox". Spell it exactly.'),
        },
      ],
    },

    {
      id: 'photos-safety',
      title: 'Photos Upload - Safety',
      subtitle: 'Four frames, always.',
      fields: [
        { id: 'photo_front', kind: 'photo', label: 'Picture of front of locker', required: true, sourceTag: 'Q51', maps: mapped('Please_provide_a_picture_of_the_front__c', 'url') },
        { id: 'photo_right', kind: 'photo', label: 'Picture of right side of locker', required: true, sourceTag: 'Q52', maps: mapped('Please_provide_a_picture_of_the_right__c', 'url') },
        { id: 'photo_left', kind: 'photo', label: 'Picture of left side of locker', required: true, sourceTag: 'Q53', maps: mapped('Please_provide_a_picture_of_the_left__c', 'url') },
        {
          id: 'photo_back',
          kind: 'photo',
          label: 'Picture of back of locker',
          required: true,
          showIf: { field: 'mount_type', op: 'notIn', value: ['Back to Back', 'Wall Mount', 'Window Mount'] },
          help: 'Skipped when the back is against something. In the source this is gated on the same Location Type answer.',
          sourceTag: 'Q54',
          maps: mapped('Please_provide_a_picture_of_the_back__c', 'url'),
        },
      ],
    },

    {
      id: 'for-core',
      title: 'For Core (Gen 2.5,3)',
      subtitle: 'Shown only for CORE hardware, derived from Access_Point_Type__c rather than asked.',
      showIf: IS_CORE,
      fields: [
        { id: 'ipc_on_arrival', kind: 'yesno', label: 'Upon arrival, was the IPC turned on?', required: true, sourceTag: 'Q18', maps: mapped('Upon_arrival_was_the_IPC_turned_on__c', 'picklist') },
        { id: 'modem_on_arrival', kind: 'yesno', label: 'Upon arrival, was the modem on?', required: true, sourceTag: 'Q19', maps: mapped('Upon_arrival_was_the_modem_on__c', 'picklist') },
        {
          id: 'rj45_led',
          kind: 'yesno',
          label: 'Upon arrival, was the LED on the RJ45 cable connected to the IPC blinking?',
          required: true,
          sourceTag: 'Q101',
          maps: mapped('Upon_arrival_was_the_LED_blinking__c', 'picklist'),
        },
        { id: 'modem_mac_photo', kind: 'photo', label: 'Picture of Modem MAC Address', required: true, sourceTag: 'Q23', maps: mapped('Photo_of_modem_serial_number__c', 'url') },
        {
          id: 'modem_mac',
          kind: 'text',
          label: 'Please provide the MAC Address of the Modem:',
          required: true,
          prefill: { source: 'kiosk.macAddress', overrideHint: 'Correct it if the modem has been swapped since.' },
          sourceTag: 'Q100',
          help: 'On the record for 78% of kiosks. Confirming beats reading it off a label in a cabinet.',
          maps: mapped('Modem_serial_number__c', 'textarea'),
        },
        { id: 'sim_photo', kind: 'photo', label: 'SIM card Number photo', required: true, sourceTag: 'Q26', maps: mapped('Photo_of_SIM_serial_number__c', 'url') },
        {
          id: 'sim_iccid',
          kind: 'text',
          label: 'Please provide ICCID on SIM card:',
          required: true,
          validate: { minLength: 18, maxLength: 22, pattern: '[0-9]{18,22}', patternMessage: 'An ICCID is 19 or 20 digits' },
          sourceTag: 'Q104',
          maps: mapped('SIM_serial_number__c', 'textarea'),
        },
      ],
    },

    {
      id: 'for-odin',
      title: 'For ODIN (Gen 4)',
      subtitle: 'Shown only for ODIN hardware. In the source, this and the CORE block together account for roughly 40 of the 124 questions.',
      showIf: IS_ODIN,
      fields: [
        { id: 'screen_in_sun', kind: 'yesno', label: 'Is the locker screen exposed to the sun?', required: true, sourceTag: 'Q20', maps: mapped('Is_Locker_exposed_to_sun__c', 'picklist') },
        {
          id: 'sunscreen_installed',
          kind: 'yesno',
          label: 'Is there a sunscreen cover installed?',
          required: true,
          showIf: { field: 'screen_in_sun', op: 'eq', value: true },
          sourceTag: 'Q21',
          maps: mapped('Is_there_a_sunscreen_cover_installed__c', 'picklist'),
        },
        { id: 'screen_functional', kind: 'yesno', label: 'Is the screen ON and functional?', required: true, sourceTag: 'Q22', maps: mapped('Is_the_locker_screen_illuminated__c', 'picklist') },
        { id: 'screen_photo', kind: 'photo', label: 'Picture of screen', required: true, sourceTag: 'Q24', maps: mapped('Take_a_photo_of_the_locker_screen__c', 'url') },
        { id: 'starter_interior_photo', kind: 'photo', label: 'Starter interior photo', required: true, sourceTag: 'Q25', maps: described('Locker_Plugged_In_Pic__c', 'url', 'Best available match; the source tag Q25 maps to a field not present in the captured 111.') },
        { id: 'service_code_ok', kind: 'yesno', label: 'Was the service code successful?', required: true, sourceTag: 'Q29', maps: mapped('Was_the_service_code_successful__c', 'picklist') },
        {
          id: 'ahls_indicated',
          kind: 'yesno',
          label: 'Did the AHLS (Locker Servicing) app clearly indicate what the issue with the Locker was?',
          required: true,
          sourceTag: 'Q30',
          maps: mapped('Did_app_indicate_issue_with_Locker__c', 'picklist'),
        },
        {
          id: 'ahls_completed',
          kind: 'yesno',
          label: 'Did you complete all steps in the AHLS app until the screen where you can enter another service code?',
          required: true,
          sourceTag: 'Q31',
          maps: mapped('Went_through_AHLS_app_process__c', 'picklist'),
        },
        {
          id: 'ahls_issues',
          kind: 'textarea',
          label: 'What were the issues?',
          required: true,
          showIf: {
            any: [
              { field: 'ahls_indicated', op: 'eq', value: false },
              { field: 'ahls_completed', op: 'eq', value: false },
              { field: 'service_code_ok', op: 'eq', value: false },
            ],
          },
          validate: { maxLength: 3000 },
          sourceTag: 'Q32',
          maps: mapped('What_were_the_issues__c', 'textarea'),
        },
      ],
    },

    {
      id: 'on-site-visit',
      title: 'On-Site Visit Questions',
      fields: [
        { id: 'locker_sweep', kind: 'yesno', label: 'Did you perform a locker sweep?', required: true, sourceTag: 'Q70', maps: mapped('Did_you_perform_Locker_Sweep__c', 'picklist') },
        {
          id: 'no_sweep_why',
          kind: 'textarea',
          label: "Why didn't you perform a locker sweep",
          required: true,
          showIf: { field: 'locker_sweep', op: 'eq', value: false },
          validate: { maxLength: 3000 },
          sourceTag: 'Q71',
          maps: mapped('Why_no_Locker_sweep__c', 'textarea'),
        },
        { id: 'slots_online', kind: 'yesno', label: 'Are all slots online?', required: true, sourceTag: 'Q72', maps: mapped('Are_all_slots_online__c', 'picklist') },
        {
          id: 'slots_pending',
          kind: 'textarea',
          label: 'What is pending for all slots to be online?',
          required: true,
          showIf: { field: 'slots_online', op: 'eq', value: false },
          validate: { maxLength: 3000 },
          sourceTag: 'Q73',
          maps: mapped('Slots_offline_reason__c', 'textarea'),
        },
        {
          id: 'enough_space',
          kind: 'yesno',
          label: 'Is there enough space for customers to open all slots?',
          required: true,
          sourceTag: 'Q74',
          maps: mapped('Enough_space_to_open__c', 'picklist'),
        },
      ],
    },

    {
      id: 'residential',
      title: 'Residential Locker',
      subtitle: 'Two questions, shown only for residential sites. Reached from the record, never asked.',
      showIf: IS_RESIDENTIAL,
      fields: [
        {
          id: 'property_manager_check',
          kind: 'yesno',
          label: 'Did you check with property manager?',
          required: true,
          sourceTag: 'Q38',
          maps: mapped('Did_you_check_in_with_property_manager__c', 'picklist'),
        },
        {
          id: 'cleaned_packages',
          kind: 'yesno',
          label: 'Did you clean packages inside the locker?',
          required: true,
          sourceTag: 'Q39',
          maps: mapped('Did_you_clean_packages_inside_locker__c', 'picklist'),
        },
      ],
    },

    {
      id: 'parts-repairs',
      title: 'Parts and Repairs',
      fields: [
        { id: 'problem_fixed', kind: 'yesno', label: 'Did you solve all the issues present with the locker?', required: true, sourceTag: 'Q40', maps: mapped('Problem_fixed__c', 'picklist') },
        {
          id: 'repair_summary',
          kind: 'textarea',
          label: 'Give a summary explaining what was repaired.',
          required: true,
          validate: { maxLength: 3000 },
          sourceTag: 'Q41',
          maps: mapped('Summary_of_Resolution__c', 'textarea'),
        },
        {
          id: 'pending_issues',
          kind: 'textarea',
          label: 'What are the pending issues?',
          required: true,
          showIf: { field: 'problem_fixed', op: 'eq', value: false },
          validate: { maxLength: 3000 },
          sourceTag: 'Q42',
          maps: mapped('Pending_Issues__c', 'textarea'),
        },
        {
          id: 'revisit_reason',
          kind: 'single',
          label: 'What is the reason a revisit is needed?',
          options: ['Parts on order', 'Specialist required', 'Site inaccessible', 'Ran out of time', 'Other'],
          required: true,
          showIf: { field: 'problem_fixed', op: 'eq', value: false },
          inferred: true,
          maps: mapped('Cancellation_Reason__c', 'picklist', 'Field name says cancellation but the survey uses it for revisit reasons. Option wording here is inferred; the source list was not recoverable.'),
        },
        { id: 'parts_ordered', kind: 'yesno', label: 'Did you order new parts/were parts missing?', required: true, sourceTag: 'Q43', maps: mapped('Parts_Ordered__c', 'picklist') },
        {
          id: 'parts_ordered_core',
          kind: 'multi',
          label: 'What parts did you order (CORE)?',
          options: [...CORE_PARTS],
          required: true,
          showIf: { all: [{ field: 'parts_ordered', op: 'eq', value: true }, IS_CORE] },
          inferred: true,
          maps: mapped('Parts_ordered_Core__c', 'multipicklist', 'Option wording inferred. Note the workflow also writes Parts_ordered_Cable_text__c and Parts_ordered_Other_text__c from the same question, so "Cable" and "Other" need free-text follow-ups.'),
        },
        {
          id: 'parts_ordered_odin',
          kind: 'multi',
          label: 'What parts did you order (ODIN)?',
          options: [...ODIN_PARTS],
          required: true,
          showIf: { all: [{ field: 'parts_ordered', op: 'eq', value: true }, IS_ODIN] },
          inferred: true,
          maps: mapped('Parts_ordered_ODIN__c', 'multipicklist', 'Option wording inferred.'),
        },
        {
          id: 'parts_ordered_other_text',
          kind: 'text',
          label: 'Which other part did you order?',
          required: true,
          showIf: {
            any: [
              { field: 'parts_ordered_core', op: 'includes', value: ['Other'] },
              { field: 'parts_ordered_odin', op: 'includes', value: ['Other'] },
            ],
          },
          sourceTag: 'Q44 (Other)',
          maps: mapped('Parts_ordered_Other_text__c', 'textarea'),
        },
        { id: 'parts_consumed', kind: 'yesno', label: 'Did you consume parts?', required: true, sourceTag: 'Q67', maps: mapped('Did_you_consume_parts__c', 'picklist') },
        {
          id: 'parts_used_core',
          kind: 'multi',
          label: 'What parts did you use (CORE)?',
          options: [...CORE_PARTS],
          required: true,
          showIf: { all: [{ field: 'parts_consumed', op: 'eq', value: true }, IS_CORE] },
          inferred: true,
          maps: mapped('Parts_consumed_Core__c', 'multipicklist', 'Option wording inferred.'),
        },
        {
          id: 'parts_used_odin',
          kind: 'multi',
          label: 'What parts did you use (ODIN)?',
          options: [...ODIN_PARTS],
          required: true,
          showIf: { all: [{ field: 'parts_consumed', op: 'eq', value: true }, IS_ODIN] },
          inferred: true,
          maps: mapped('Parts_consumed_ODIN__c', 'multipicklist', 'Option wording inferred.'),
        },
        {
          id: 'parts_used_other_text',
          kind: 'text',
          label: 'Which other part did you use?',
          required: true,
          showIf: {
            any: [
              { field: 'parts_used_core', op: 'includes', value: ['Other'] },
              { field: 'parts_used_odin', op: 'includes', value: ['Other'] },
            ],
          },
          sourceTag: 'Q68 (Other)',
          maps: mapped('Parts_consumed_Other_text__c', 'textarea'),
        },
        {
          id: 'tool_missing',
          kind: 'yesno',
          label: 'Is there any tool missing that was required for the job?',
          required: true,
          sourceTag: 'Q49',
          maps: mapped('Is_there_a_tool_missing_for_the_job__c', 'picklist'),
        },
        {
          id: 'tools_missing_which',
          kind: 'textarea',
          label: 'What tools were you missing?',
          required: true,
          showIf: { field: 'tool_missing', op: 'eq', value: true },
          validate: { maxLength: 3000 },
          sourceTag: 'Q50',
          maps: mapped('If_not_what_tool_were_you_missing__c', 'textarea'),
        },
        {
          id: 'issue_not_in_ticket',
          kind: 'yesno',
          label: 'Was there any issue that was not in the ticket?',
          required: true,
          sourceTag: 'Q47',
          maps: mapped('Issues_not_in_ticket__c', 'picklist'),
        },
        {
          id: 'issue_not_in_ticket_detail',
          kind: 'textarea',
          label: 'Describe the issue that is not in the ticket:',
          required: true,
          showIf: { field: 'issue_not_in_ticket', op: 'eq', value: true },
          validate: { maxLength: 3000 },
          sourceTag: 'Q48',
          maps: mapped('Description_of_issues_not_in_ticket__c', 'textarea'),
        },
      ],
    },

    {
      id: 'end-of-visit',
      title: 'End-of-Visit Questions',
      fields: [
        {
          id: 'health_confirmed',
          kind: 'multi',
          label: 'Did you confirm Locker health with TTK or OTS?',
          options: ['TTK', 'OTS', 'Neither'],
          required: true,
          validate: { minSelections: 1 },
          sourceTag: 'Q75',
          inferred: true,
          maps: mapped('Health_confirmed__c', 'multipicklist', 'Option wording inferred.'),
        },
        {
          id: 'contacted_ots',
          kind: 'yesno',
          label: 'Did You try to contact with OTS support?',
          required: true,
          sourceTag: 'Q117',
          maps: mapped('Did_You_try_to_contact_with_OTS_suppor__c', 'picklist'),
        },
        {
          id: 'ots_channel',
          kind: 'single',
          label: 'Did You call on support line or use chat?',
          options: ['Support line', 'Chat'],
          required: true,
          showIf: { field: 'contacted_ots', op: 'eq', value: true },
          inferred: true,
          maps: mapped('Did_You_call_on_support_line_or_use_chat__c', 'picklist', 'Option wording inferred.'),
        },
        {
          id: 'ots_reached',
          kind: 'yesno',
          label: 'Did You manage to get in contact with OTS support?',
          required: true,
          showIf: { field: 'contacted_ots', op: 'eq', value: true },
          sourceTag: 'Q119',
          maps: mapped('Manage_to_get_in_contact_with_OTS__c', 'picklist'),
        },
        {
          id: 'ots_wait',
          kind: 'single',
          label: 'What was waiting time for OTS response?',
          options: ['Under 5 minutes', '5 to 15 minutes', '15 to 30 minutes', 'Over 30 minutes', 'No response'],
          required: true,
          showIf: { field: 'contacted_ots', op: 'eq', value: true },
          sourceTag: 'Q120',
          inferred: true,
          maps: mapped('What_was_waiting_time_for_OTS_response__c', 'picklist', 'Option wording inferred.'),
        },
        {
          id: 'ots_helped',
          kind: 'yesno',
          label: 'Did OTS help you solve the problem?',
          required: true,
          showIf: { field: 'ots_reached', op: 'eq', value: true },
          sourceTag: 'Q77',
          maps: mapped('OTS_helped_solve_issue__c', 'picklist'),
        },
        {
          id: 'ots_person',
          kind: 'text',
          label: 'What was the name of the OTS person you spoke to?',
          showIf: { field: 'ots_reached', op: 'eq', value: true },
          sourceTag: 'Q78',
          maps: mapped('What_is_name_of_OTS_person_you_spoke_to__c', 'textarea'),
        },
        {
          id: 'ots_screenshot',
          kind: 'photo',
          label: 'Please provide a screenshot of the conversation you had with OTS (optional)',
          showIf: { field: 'ots_channel', op: 'eq', value: 'Chat' },
          sourceTag: 'Q113',
          maps: mapped('Pic_OTS_Conversation__c', 'url'),
        },
        {
          id: 'visit_end',
          kind: 'time',
          label: 'Enter the Visit End Time with 24-hour format (HH:MM):',
          required: true,
          validate: { pattern: '([01][0-9]|2[0-3]):[0-5][0-9]', patternMessage: 'Use HH:MM, 24-hour' },
          sourceTag: 'Q92',
          maps: mapped('Visit_End_Time__c', 'time', 'string(30) per the describe.'),
        },
      ],
    },
  ],
};
